"use client"

import type React from "react"
import { useEffect, useMemo, useState } from "react"
import {
  FaRupeeSign,
  FaPlus,
  FaEdit,
  FaEye,
  FaFilter,
  FaMoneyBillWave,
  FaCheckCircle,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaFileExcel,
  FaSpinner,
  FaTimes,
} from "react-icons/fa"
import { FiFilter, FiX, FiRefreshCw, FiSearch as FiSearchIcon, FiBookOpen, FiCalendar, FiUser, FiExternalLink } from "react-icons/fi"
import { Link } from "react-router-dom"
import { Pagination as PaginationComponent } from "../../../utils/pagination.utils"
import type { SelectOption } from "../../../utils/select-with-search"
import { SearchableSelect } from "../../../utils/select-with-search"
import { TrainingPayment, Pagination } from "../types"
import { useAuth } from "../../../provider/auth.provider"
import { ExcelDownload } from "../../../utils/excel.utils"
import toast from "react-hot-toast"
import api from "../../../libs/axios"

type TrainingPaymentExportRow = Record<string, string | number | boolean | null>

export interface PaymentFilters {
  [key: string]: string | undefined
  search?: string
  payment_status?: string
  payment_type?: string
  training_institute_id?: string
  training_institute_label?: string
  training_type_id?: string
  training_type_label?: string
  pi_number?: string
  pi_date_from?: string
  pi_date_to?: string
  invoice_number?: string
  invoice_date_from?: string
  invoice_date_to?: string
  pi_basic_amount_min?: string
  pi_basic_amount_max?: string
  pi_tax_amount_min?: string
  pi_tax_amount_max?: string
  pi_total_amount_min?: string
  pi_total_amount_max?: string
  per_person_amount_min?: string
  per_person_amount_max?: string
  no_of_candidates_min?: string
  no_of_candidates_max?: string
  created_from?: string
  created_to?: string
  paid_from?: string
  paid_to?: string
}

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  filters: PaymentFilters
  onFiltersChange: (filters: PaymentFilters) => void
  onApply: () => void
  onReset: () => void
  trainingTypeOptions: SelectOption[]
  trainingInstituteOptions: SelectOption[]
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  trainingTypeOptions,
  trainingInstituteOptions,
}) => {
  const [activeTab, setActiveTab] = useState("basic")
  const [selectedTrainingType, setSelectedTrainingType] = useState<SelectOption | null>(null)
  const [selectedInstitute, setSelectedInstitute] = useState<SelectOption | null>(null)

  useEffect(() => {
    if (isOpen) {
      setActiveTab("basic")
    }
  }, [isOpen])

  useEffect(() => {
    if (filters.training_type_id && filters.training_type_label) {
      setSelectedTrainingType({
        id: Number(filters.training_type_id),
        value: filters.training_type_id,
        label: filters.training_type_label,
      })
    } else {
      setSelectedTrainingType(null)
    }
  }, [filters.training_type_id, filters.training_type_label])

  useEffect(() => {
    if (filters.training_institute_id && filters.training_institute_label) {
      setSelectedInstitute({
        id: Number(filters.training_institute_id),
        value: filters.training_institute_id,
        label: filters.training_institute_label,
      })
    } else {
      setSelectedInstitute(null)
    }
  }, [filters.training_institute_id, filters.training_institute_label])

  const tabs = useMemo(
    () => [
      { id: "basic", label: "Basic Info", icon: FiSearchIcon },
      { id: "pi", label: "PI Details", icon: FiBookOpen },
      { id: "amounts", label: "Amounts", icon: FaRupeeSign },
      { id: "candidates", label: "Candidates", icon: FiUser },
      { id: "dates", label: "Dates", icon: FiCalendar },
    ],
    [],
  )

  if (!isOpen) return null

  const handleInputChange = (field: keyof PaymentFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    })
  }

  const handleTrainingTypeSelect = (option: SelectOption | null) => {
    setSelectedTrainingType(option)
    onFiltersChange({
      ...filters,
      training_type_id: option ? String(option.value) : undefined,
      training_type_label: option ? option.label : undefined,
    })
  }

  const handleInstituteSelect = (option: SelectOption | null) => {
    setSelectedInstitute(option)
    onFiltersChange({
      ...filters,
      training_institute_id: option ? String(option.value) : undefined,
      training_institute_label: option ? option.label : undefined,
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-visible">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.id ? "border-blue-500 text-blue-600 bg-white" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === "basic" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Search by PI number..."
                  value={filters.search || ""}
                  onChange={(e) => handleInputChange("search", e.target.value)}
                />
              </div> */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  value={filters.payment_status || ""}
                  onChange={(e) => handleInputChange("payment_status", e.target.value)}
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Type</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  value={filters.payment_type || ""}
                  onChange={(e) => handleInputChange("payment_type", e.target.value)}
                >
                  <option value="">All</option>
                  <option value="advance">Advance</option>
                  <option value="post_payment">Post Payment</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Institute</label>
                <SearchableSelect
                  value={selectedInstitute}
                  onChange={handleInstituteSelect}
                  options={trainingInstituteOptions}
                  placeholder="Search institutes"
                  className="w-full"
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Training Type</label>
                <SearchableSelect
                  value={selectedTrainingType}
                  onChange={handleTrainingTypeSelect}
                  options={trainingTypeOptions}
                  placeholder="Search training types"
                  className="w-full"
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
            </div>
          )}

          {activeTab === "pi" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PI Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="e.g., PI-2026-001"
                  value={filters.pi_number || ""}
                  onChange={(e) => handleInputChange("pi_number", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="e.g., INV-2026-001"
                  value={filters.invoice_number || ""}
                  onChange={(e) => handleInputChange("invoice_number", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PI Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.pi_date_from || ""}
                  onChange={(e) => handleInputChange("pi_date_from", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PI Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.pi_date_to || ""}
                  onChange={(e) => handleInputChange("pi_date_to", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.invoice_date_from || ""}
                  onChange={(e) => handleInputChange("invoice_date_from", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.invoice_date_to || ""}
                  onChange={(e) => handleInputChange("invoice_date_to", e.target.value)}
                />
              </div>
            </div>
          )}

          {activeTab === "amounts" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { label: "PI Basic Amount Min", field: "pi_basic_amount_min" },
                { label: "PI Basic Amount Max", field: "pi_basic_amount_max" },
                { label: "PI Tax Amount Min", field: "pi_tax_amount_min" },
                { label: "PI Tax Amount Max", field: "pi_tax_amount_max" },
                { label: "PI Total Amount Min", field: "pi_total_amount_min" },
                { label: "PI Total Amount Max", field: "pi_total_amount_max" },
                { label: "Per Person Amount Min", field: "per_person_amount_min" },
                { label: "Per Person Amount Max", field: "per_person_amount_max" },
              ].map((input) => (
                <div key={input.field}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{input.label}</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={filters[input.field as keyof PaymentFilters] || ""}
                    onChange={(e) => handleInputChange(input.field as keyof PaymentFilters, e.target.value)}
                  />
                </div>
              ))}
            </div>
          )}

          {activeTab === "candidates" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">No. of Candidates (Min)</label>
                <input
                  type="number"
                  min="0"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.no_of_candidates_min || ""}
                  onChange={(e) => handleInputChange("no_of_candidates_min", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">No. of Candidates (Max)</label>
                <input
                  type="number"
                  min="0"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.no_of_candidates_max || ""}
                  onChange={(e) => handleInputChange("no_of_candidates_max", e.target.value)}
                />
              </div>
            </div>
          )}

          {activeTab === "dates" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_from || ""}
                  onChange={(e) => handleInputChange("created_from", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_to || ""}
                  onChange={(e) => handleInputChange("created_to", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Paid Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.paid_from || ""}
                  onChange={(e) => handleInputChange("paid_from", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Paid Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus-border-blue-500 transition-colors"
                  value={filters.paid_to || ""}
                  onChange={(e) => handleInputChange("paid_to", e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            type="button"
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApply}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
            >
              <FiSearchIcon className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Inline "view more" cell for the linked training/candidate names, mirroring the
// project-names pattern used elsewhere (shows the first few, then "+N more").
const TrainingNamesCell: React.FC<{ candidates?: TrainingPayment["payment_candidates"] }> = ({ candidates }) => {
  const [showAll, setShowAll] = useState(false)
  const list = Array.isArray(candidates) ? candidates : []
  const SHOW_COUNT = 2
  if (list.length === 0) return <span className="text-gray-400">-</span>
  const visible = showAll ? list : list.slice(0, SHOW_COUNT)
  return (
    <div className="max-w-xs space-y-1">
      {visible.map((pc, idx) => (
        <div key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded">
          {pc.training_request?.candidate_name || `Request #${pc.training_request_id}`}
        </div>
      ))}
      {list.length > SHOW_COUNT && (
        <button
          type="button"
          onClick={() => setShowAll((prev) => !prev)}
          className="text-xs font-medium text-blue-700 hover:underline"
        >
          {showAll ? "View less" : `+${list.length - SHOW_COUNT} more`}
        </button>
      )}
    </div>
  )
}

interface PaymentsListProps {
  payments: TrainingPayment[]
  pagination: Pagination
  onStatusFilter: (status: string) => void
  onTypeFilter: (type: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAdd: () => void
  onEdit: (payment: TrainingPayment) => void
  onMarkPaid: (id: number, reference: string, paymentDate?: string) => void
  loading?: boolean
  sortConfig?: { field: string; direction: "ASC" | "DESC" }
  onSort?: (field: string, direction: "ASC" | "DESC") => void
  onAdvancedFilter?: (filters: PaymentFilters) => void
  advancedFilters?: PaymentFilters
  trainingTypeOptions?: SelectOption[]
  trainingInstituteOptions?: SelectOption[]
}

export const PaymentsList: React.FC<PaymentsListProps> = ({
  payments,
  pagination,
  onStatusFilter,
  onTypeFilter,
  onPageChange,
  onLimitChange,
  onAdd,
  onEdit,
  onMarkPaid,
  loading = false,
  sortConfig,
  onSort,
  onAdvancedFilter,
  advancedFilters = {},
  trainingTypeOptions = [],
  trainingInstituteOptions = [],
}) => {
  const { hasPermission } = useAuth()
  const canCreatePayment = hasPermission("TrainingPayment", "create")
  const canEditPayment = hasPermission("TrainingPayment", "edit")

  const [statusFilter, setStatusFilter] = useState("")
  const [typeFilter, setTypeFilter] = useState("")
  const [paymentRefModal, setPaymentRefModal] = useState<{ id: number; show: boolean }>({ id: 0, show: false })
  const [paymentRef, setPaymentRef] = useState("")
  const [paymentDate, setPaymentDate] = useState("")
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [linkedRequestsModal, setLinkedRequestsModal] = useState<{ show: boolean; requests: any[] }>({ show: false, requests: [] })
  const [linkedRequestsLoading, setLinkedRequestsLoading] = useState(false)
  const [viewPayment, setViewPayment] = useState<{ show: boolean; payment: TrainingPayment | null; documents: any[] }>({ show: false, payment: null, documents: [] })
  const [viewLoading, setViewLoading] = useState(false)

  const handleViewLinkedRequests = async (paymentId: number) => {
    setLinkedRequestsLoading(true)
    try {
      const res = await api.get(`/training/payments/${paymentId}`)
      const candidates = res.data.data?.training_payment?.payment_candidates || []
      setLinkedRequestsModal({ show: true, requests: candidates.map((c: any) => ({ ...c.training_request, candidate_payment_status: c.payment_status })).filter((r: any) => r?.id) })
    } catch {
      toast.error("Failed to load linked requests")
    } finally {
      setLinkedRequestsLoading(false)
    }
  }

  const handleViewPayment = async (payment: TrainingPayment) => {
    setViewLoading(true)
    try {
      const [paymentRes, docsRes] = await Promise.allSettled([
        api.get(`/training/payments/${payment.id}`),
        api.get(`/training/payments/${payment.id}/documents`),
      ])
      const fullPayment = paymentRes.status === "fulfilled"
        ? (paymentRes.value.data.data?.training_payment || payment)
        : payment
      const docs = docsRes.status === "fulfilled"
        ? (docsRes.value.data.data?.documents || [])
        : []
      setViewPayment({ show: true, payment: fullPayment, documents: docs })
    } catch {
      setViewPayment({ show: true, payment, documents: [] })
    } finally {
      setViewLoading(false)
    }
  }
  const [filters, setFilters] = useState<PaymentFilters>(advancedFilters)
  const [showExcelModal, setShowExcelModal] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<TrainingPaymentExportRow[]>([])

  useEffect(() => {
    setFilters(advancedFilters)
  }, [advancedFilters])

  const getActiveFiltersCount = () =>
    Object.entries(advancedFilters)
      .filter(([key, value]) => !key.endsWith("_label") && value !== undefined && value !== "")
      .length

  const handleFiltersChange = (updatedFilters: PaymentFilters) => {
    setFilters(updatedFilters)
  }

  const handleResetFilters = () => {
    setFilters({})
    onAdvancedFilter?.({})
  }

  const handleApplyFilters = () => {
    onAdvancedFilter?.(filters)
    setIsFilterModalOpen(false)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-"
    return new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
  }

  const formatAmount = (amount?: number) => {
    if (!amount) return "-"
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount)
  }

  const formatDateTimeValue = (dateString?: string | null) => {
    if (!dateString) return "-"
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return "-"
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusLabel = (status?: string) => {
    if (status === "paid") return "Paid"
    if (status === "pending") return "Pending"
    return status || "-"
  }

  const getPaymentTypeLabel = (type?: string) => {
    if (type === "advance") return "Advance"
    if (type === "post_payment") return "Post Payment"
    return type || "-"
  }

  const getUserName = (user?: { first_name?: string; last_name?: string } | null) => {
    if (!user) return "-"
    return [user.first_name, user.last_name].filter(Boolean).join(" ") || "-"
  }

  const buildExportParams = (page: number, limit: number) => {
    const params: Record<string, string | number | boolean> = { page, limit }

    if (statusFilter) params.payment_status = statusFilter
    if (typeFilter) params.payment_type = typeFilter

    Object.entries(filters || {}).forEach(([key, value]) => {
      if (key.endsWith("_label")) return
      if (value !== undefined && value !== null && value !== "") {
        params[key] = value
      }
    })

    if (sortConfig?.field) {
      params.sort_by = sortConfig.field
      params.sort_order = sortConfig.direction
    }

    return params
  }

  const mapPaymentForExport = (payment: TrainingPayment): TrainingPaymentExportRow => {
    const candidates = Array.isArray(payment.payment_candidates) ? payment.payment_candidates : [];
    const trainingIds = candidates.map(pc => pc.training_request?.request_id || `#${pc.training_request_id}`).join(", ") || "-";
    const trainingNames = candidates.map(pc => pc.training_request?.candidate_name || "-").join(", ") || "-";
    const trainingCount = candidates.length;
    // The PI names its own set of training IDs; the invoice-linked candidates
    // are a separate list, so the two counts are reported side by side.
    const piCandidateIds = Array.isArray((payment as any).pi_candidate_ids) ? (payment as any).pi_candidate_ids : [];
    const piTrainingCount = piCandidateIds.length;
    const piTrainingIds = piCandidateIds
      .map((id: number) => {
        const linked = candidates.find(pc => Number(pc.training_request_id) === Number(id));
        const piRequest = (payment as any).pi_training_requests?.find((r: any) => Number(r.id) === Number(id));
        return linked?.training_request?.request_id || piRequest?.request_id || `#${id}`;
      })
      .join(", ") || "-";
    
    return {
      "Institute Name": payment.training_institute?.institute_name || "-",
      "Institute Short Name": payment.training_institute?.institute_short_name || "-",
      "Institute Contact": payment.training_institute?.contact_person || "-",
      "Institute Phone": payment.training_institute?.contact_phone || "-",
      "Institute Email": payment.training_institute?.contact_email || "-",
      "Training Type": payment.training_type?.training_name || "-",
      "PI Number": payment.pi_number || "-",
      "PI Date": payment.pi_date ? formatDate(payment.pi_date) : "-",
      "Invoice Number": payment.invoice_number || "-",
      "Invoice Date": payment.invoice_date ? formatDate(payment.invoice_date) : "-",
      "PI Basic Amount": payment.pi_basic_amount ?? "-",
      "PI Tax Amount": payment.pi_tax_amount ?? "-",
      "PI Total Amount": payment.pi_total_amount ?? "-",
      "Invoice Basic Amount": (payment as any).invoice_basic_amount ?? "-",
      "Invoice Tax Amount": (payment as any).invoice_tax_amount ?? "-",
      "Invoice Total Amount": (payment as any).invoice_total_amount ?? "-",
      "Per Person Amount": payment.per_person_amount ?? "-",
      "No. of Candidates": payment.no_of_candidates ?? "-",
      "PI Training ID Count": piTrainingCount,
      "PI Training IDs": piTrainingIds,
      "Training ID Count": trainingCount,
      "Training IDs": trainingIds,
      "Training Names": trainingNames,
      "Stage": payment.stage || "-",
      "Payment Type": getPaymentTypeLabel(payment.payment_type),
      "Payment Status": getStatusLabel(payment.payment_status),
      "Payment Reference": payment.payment_reference || "-",
      "Paid At": formatDateTimeValue(payment.paid_at),
      "Paid By": getUserName(payment.payer),
      "Created By": getUserName(payment.creator),
      "Creator Email": payment.creator?.email || "-",
      "Created At": formatDateTimeValue(payment.created_at),
      "Updated At": formatDateTimeValue(payment.updated_at),
    }
  }

  const fetchExportData = async () => {
    if (exporting) return

    try {
      setExporting(true)
      setExportData([])
      const pageSize = 100
      let page = 1
      let hasMore = true
      const allPayments: TrainingPayment[] = []

      while (hasMore) {
        const params = buildExportParams(page, pageSize)
        const response = await api.get("/training/payments/list", { params })

        if (!response.data?.success) {
          throw new Error(response.data?.message || "Failed to fetch payments")
        }

        const data = response.data.data || {}
        const fetched: TrainingPayment[] = data.training_payments || data.payments || []
        const paginationInfo = data.pagination

        allPayments.push(...fetched)

        const nextPageAvailable = paginationInfo?.has_next_page ??
          (paginationInfo?.total_pages ? page < paginationInfo.total_pages : fetched.length === pageSize)

        if (!fetched.length || !nextPageAvailable) {
          hasMore = false
        } else {
          page += 1
        }
      }

      if (!allPayments.length) {
        toast.error("No training payments found for export")
        return
      }

      setExportData(allPayments.map(mapPaymentForExport))
      setShowExcelModal(true)
    } catch (error: any) {
      console.error("Error exporting training payments:", error)
      toast.error(error?.response?.data?.message || "Failed to prepare export data")
    } finally {
      setExporting(false)
    }
  }

  const handleMarkPaid = () => {
    if (paymentRefModal.id && paymentRef.trim()) {
      onMarkPaid(paymentRefModal.id, paymentRef, paymentDate || undefined)
      setPaymentRefModal({ id: 0, show: false })
      setPaymentRef("")
      setPaymentDate("")
    }
  }

  const handleColumnSort = (field: string) => {
    if (!onSort) return
    const direction = sortConfig?.field === field && sortConfig?.direction === "ASC" ? "DESC" : "ASC"
    onSort(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (sortConfig?.field !== field) return <FaSort className="w-3 h-3 text-gray-400" />
    if (sortConfig.direction === "ASC") return <FaSortUp className="w-3 h-3 text-blue-600" />
    return <FaSortDown className="w-3 h-3 text-blue-600" />
  }

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Training Payments</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              {exporting ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <FaFileExcel className="w-4 h-4" />
                  Export Excel
                </>
              )}
            </button>
            {canCreatePayment && (
              <button onClick={onAdd} className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center gap-2 transition-colors text-sm font-medium shadow-sm">
                <FaPlus className="w-4 h-4" /> New Payment
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="w-full sm:w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Payment Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                const value = e.target.value
                setStatusFilter(value)
                onStatusFilter(value)
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div className="w-full sm:w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Payment Type</label>
            <select
              value={typeFilter}
              onChange={(e) => {
                const value = e.target.value
                setTypeFilter(value)
                onTypeFilter(value)
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white"
            >
              <option value="">All</option>
              <option value="advance">Advance</option>
              <option value="post_payment">Post Payment</option>
            </select>
          </div>
          <div className="w-full sm:w-auto flex items-end">
            <button
              type="button"
              onClick={() => setIsFilterModalOpen(true)}
              className={`inline-flex items-center gap-2 w-full sm:w-auto justify-center px-4 py-2 border rounded text-sm font-medium transition-colors ${
                getActiveFiltersCount() > 0 ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">Actions</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[160px]">Institute</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Training Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">
                <button type="button" onClick={() => handleColumnSort("pi_number")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  PI Number {getSortIcon("pi_number")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[110px]">
                <button type="button" onClick={() => handleColumnSort("pi_date")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  PI Date {getSortIcon("pi_date")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <button type="button" onClick={() => handleColumnSort("pi_total_amount")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  PI Total Amount {getSortIcon("pi_total_amount")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">
                <button type="button" onClick={() => handleColumnSort("invoice_number")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Invoice Number {getSortIcon("invoice_number")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[110px]">
                <button type="button" onClick={() => handleColumnSort("invoice_date")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Invoice Date {getSortIcon("invoice_date")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">Invoice Total Amount</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">PI Training ID Count</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Invoice Training IDs Count</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">Training Names</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">
                <button type="button" onClick={() => handleColumnSort("per_person_amount")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Per Person {getSortIcon("per_person_amount")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[80px]">
                <button type="button" onClick={() => handleColumnSort("no_of_candidates")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Candidates {getSortIcon("no_of_candidates")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[190px]">PI Invoice Payment Stage</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">
                <button type="button" onClick={() => handleColumnSort("payment_type")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Payment Type {getSortIcon("payment_type")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[80px]">
                <button type="button" onClick={() => handleColumnSort("payment_status")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Cashier Status {getSortIcon("payment_status")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <button type="button" onClick={() => handleColumnSort("paid_at")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Payment Date {getSortIcon("paid_at")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Ref. No.</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <button type="button" onClick={() => handleColumnSort("created_at")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Created At {getSortIcon("created_at")}
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={20} className="px-4 py-12 text-center"><div className="flex flex-col items-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div><span className="text-sm text-gray-600">Loading payments...</span></div></td></tr>
            ) : payments.length === 0 ? (
              <tr><td colSpan={20} className="px-4 py-12 text-center"><div className="flex flex-col items-center"><div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3"><FaMoneyBillWave className="w-6 h-6 text-gray-400" /></div><p className="text-sm text-gray-500">No payments found</p></div></td></tr>
            ) : (
              payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleViewPayment(payment)} disabled={viewLoading} className="text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-50 rounded" title="View Details"><FaEye className="w-4 h-4" /></button>
                      {canEditPayment && (
                        <button onClick={() => onEdit(payment)} className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded" title="Edit"><FaEdit className="w-4 h-4" /></button>
                      )}
                      {payment.payment_status === 'pending' && canEditPayment && (
                        <button onClick={() => setPaymentRefModal({ id: payment.id, show: true })} className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded" title="Mark Paid"><FaCheckCircle className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => handleViewLinkedRequests(payment.id)} disabled={linkedRequestsLoading} className="text-purple-600 hover:text-purple-800 p-2 hover:bg-purple-50 rounded" title="View Linked Requests">
                        <FiExternalLink className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{payment.training_institute?.institute_name || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{payment.training_type?.training_name || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{payment.pi_number || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{payment.pi_date ? formatDate(payment.pi_date) : "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatAmount(payment.pi_total_amount)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{payment.invoice_number || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{(payment as any).invoice_date ? formatDate((payment as any).invoice_date) : "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{(payment as any).invoice_total_amount != null ? formatAmount((payment as any).invoice_total_amount) : "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{Array.isArray((payment as any).pi_candidate_ids) ? (payment as any).pi_candidate_ids.length : 0}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{Array.isArray(payment.payment_candidates) ? payment.payment_candidates.length : 0}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    <TrainingNamesCell candidates={payment.payment_candidates} />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{formatAmount(payment.per_person_amount)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{payment.no_of_candidates || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{payment.stage || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${payment.payment_type === 'advance' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`}>
                      {payment.payment_type === 'advance' ? 'Advance' : 'Post Payment'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${payment.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {payment.payment_status === 'paid' ? 'Paid' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{payment.paid_at ? formatDate(payment.paid_at) : "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{payment.payment_reference || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(payment.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationComponent pagination={pagination} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        trainingTypeOptions={trainingTypeOptions}
        trainingInstituteOptions={trainingInstituteOptions}
      />
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="training_payments"
        title="Export Training Payments"
        description="Download comprehensive training payment details"
      />

      {/* Linked Requests Modal */}
      {linkedRequestsModal.show && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setLinkedRequestsModal({ show: false, requests: [] })} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Linked Training Requests</h3>
              {linkedRequestsModal.requests.length === 0 ? (
                <p className="text-sm text-gray-500">No linked training requests found.</p>
              ) : (
                <div className="space-y-2">
                  {linkedRequestsModal.requests.map((req: any) => (
                    <div key={req.id} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{req.candidate_name}</p>
                        <p className="text-xs text-gray-500 font-mono">{req.request_id}</p>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium mt-1 ${req.candidate_payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                          {req.candidate_payment_status === "paid" ? "Paid" : "Pending"}
                        </span>
                      </div>
                      <Link to={`/training/requests/${req.id}`} className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 hover:underline">
                        <FiExternalLink className="w-3 h-3" /> Workflow
                      </Link>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 flex justify-end">
                <button onClick={() => setLinkedRequestsModal({ show: false, requests: [] })} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50">Close</button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* View Payment Offcanvas */}
      {viewPayment.show && viewPayment.payment && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setViewPayment({ show: false, payment: null, documents: [] })} />
          <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h3 className="font-semibold text-gray-800">Payment Details</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {viewPayment.payment.training_institute?.institute_name} · {viewPayment.payment.training_type?.training_name}
                </p>
              </div>
              <button onClick={() => setViewPayment({ show: false, payment: null, documents: [] })} className="p-1.5 hover:bg-gray-100 rounded">
                <FaTimes className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">Payment Type</p><p className="font-medium capitalize">{viewPayment.payment.payment_type?.replace(/_/g, " ")}</p></div>
                <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">Status</p><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${viewPayment.payment.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>{viewPayment.payment.payment_status}</span></div>
                {viewPayment.payment.pi_number && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">PI Number</p><p className="font-mono font-medium">{viewPayment.payment.pi_number}</p></div>}
                {viewPayment.payment.pi_date && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">PI Date</p><p className="font-medium">{formatDate(viewPayment.payment.pi_date)}</p></div>}
                {viewPayment.payment.invoice_number && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">Invoice Number</p><p className="font-mono font-medium">{viewPayment.payment.invoice_number}</p></div>}
                {viewPayment.payment.invoice_date && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">Invoice Date</p><p className="font-medium">{formatDate(viewPayment.payment.invoice_date)}</p></div>}
                {viewPayment.payment.pi_basic_amount != null && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">PI Basic Amount</p><p className="font-medium">{formatAmount(viewPayment.payment.pi_basic_amount)}</p></div>}
                {viewPayment.payment.pi_tax_amount != null && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">PI Tax Amount</p><p className="font-medium">{formatAmount(viewPayment.payment.pi_tax_amount)}</p></div>}
                {viewPayment.payment.pi_total_amount != null && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">PI Total Amount</p><p className="font-medium text-blue-800">{formatAmount(viewPayment.payment.pi_total_amount)}</p></div>}
                {(viewPayment.payment as any).invoice_basic_amount != null && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">Invoice Basic Amount</p><p className="font-medium">{formatAmount((viewPayment.payment as any).invoice_basic_amount)}</p></div>}
                {(viewPayment.payment as any).invoice_tax_amount != null && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">Invoice Tax Amount</p><p className="font-medium">{formatAmount((viewPayment.payment as any).invoice_tax_amount)}</p></div>}
                {(viewPayment.payment as any).invoice_total_amount != null && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">Invoice Total Amount</p><p className="font-medium text-green-800">{formatAmount((viewPayment.payment as any).invoice_total_amount)}</p></div>}
                {viewPayment.payment.per_person_amount != null && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">Per Person Amount</p><p className="font-medium">{formatAmount(viewPayment.payment.per_person_amount)}</p></div>}
                {viewPayment.payment.no_of_candidates != null && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">No. of Candidates</p><p className="font-medium">{viewPayment.payment.no_of_candidates}</p></div>}
                {viewPayment.payment.paid_at && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">Payment Date</p><p className="font-medium">{formatDate(viewPayment.payment.paid_at)}</p></div>}
                {viewPayment.payment.payment_reference && <div className="bg-gray-50 rounded p-3"><p className="text-gray-500 text-xs mb-1">Reference No.</p><p className="font-mono font-medium">{viewPayment.payment.payment_reference}</p></div>}
              </div>

              {/* Linked Training IDs */}
              {Array.isArray((viewPayment.payment as any).payment_candidates) && (viewPayment.payment as any).payment_candidates.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Invoice Training IDs</p>
                  <div className="space-y-1">
                    {(viewPayment.payment as any).payment_candidates.map((pc: any) => (
                      <div key={pc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <span className="font-mono text-gray-700">{pc.training_request?.request_id || `#${pc.training_request_id}`}</span>
                        <span className="text-gray-500 text-xs">{pc.training_request?.candidate_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* PI Training IDs */}
              {Array.isArray((viewPayment.payment as any).pi_candidate_ids) && (viewPayment.payment as any).pi_candidate_ids.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">PI Training IDs</p>
                  <div className="space-y-1">
                    {(viewPayment.payment as any).pi_candidate_ids.map((id: number) => {
                      const candidate = (viewPayment.payment as any).payment_candidates?.find((pc: any) => Number(pc.training_request_id) === Number(id))
                      const piRequest = (viewPayment.payment as any).pi_training_requests?.find((request: any) => Number(request.id) === Number(id))
                      return (
                        <div key={id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                          <span className="font-mono text-gray-700">{candidate?.training_request?.request_id || piRequest?.request_id || `#${id}`}</span>
                          <span className="text-gray-500 text-xs">{candidate?.training_request?.candidate_name || piRequest?.candidate_name || ""}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Documents */}
              {(() => {
                const invoiceDocs = viewPayment.documents.filter((d: any) => d.document_type === "invoice");
                const pibDocs = viewPayment.documents.filter((d: any) => d.document_type === "pib");
                const otherDocs = viewPayment.documents.filter((d: any) => d.document_type !== "invoice" && d.document_type !== "pib");
                const renderDocList = (docs: any[]) => docs.map((doc: any) => (
                  <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm border border-gray-200">
                    <p className="text-gray-700 truncate">{doc.document_name}</p>
                    <a href={doc.document_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-800 hover:underline text-xs ml-2 shrink-0">
                      View <FiExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                ));
                return (
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Invoice Documents ({invoiceDocs.length})</p>
                      {invoiceDocs.length > 0 ? <div className="space-y-2">{renderDocList(invoiceDocs)}</div> : <p className="text-sm text-gray-400 text-center py-2">No invoice documents</p>}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">PIB Documents ({pibDocs.length})</p>
                      {pibDocs.length > 0 ? <div className="space-y-2">{renderDocList(pibDocs)}</div> : <p className="text-sm text-gray-400 text-center py-2">No PIB documents</p>}
                    </div>
                    {otherDocs.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Other Documents ({otherDocs.length})</p>
                        <div className="space-y-2">{renderDocList(otherDocs)}</div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="border-t p-4 flex justify-end gap-3">
              {canEditPayment && (
                <button
                  onClick={() => { setViewPayment({ show: false, payment: null, documents: [] }); onEdit(viewPayment.payment!) }}
                  className="px-4 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900"
                >
                  Edit
                </button>
              )}
              <button onClick={() => setViewPayment({ show: false, payment: null, documents: [] })} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700">Close</button>
            </div>
          </div>
        </>
      )}

      {/* Mark Paid Modal */}
      {paymentRefModal.show && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setPaymentRefModal({ id: 0, show: false })} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Mark Payment as Paid</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Reference *</label>
                <input type="text" value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="e.g., NEFT-123456789" />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => { setPaymentRefModal({ id: 0, show: false }); setPaymentRef(""); setPaymentDate("") }} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                <button onClick={handleMarkPaid} disabled={!paymentRef.trim()} className="px-4 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50">Confirm</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
